import { localStorageValue } from "./LocalStorage"
import { parseFeatureList, resolveFeatureFlags } from "app-base/runtime/feature-flags"

// General, product-agnostic client-side feature flags. Every flag defaults to
// OFF -- there is no built-in list of "known" flags here, any caller can pass
// an arbitrary flag name -- so new code can ship dark behind a flag without a
// server change. A flag turns on via either a developer-only override (the
// `logbook-features` localStorage key or a `?features=` query param, both
// accepting a comma/whitespace separated list of flag names) or the
// `ALWAYS_ON_FEATURES` code-level list below, which ships to everyone. This is
// wired into `App.hasFeature` (see App.tsx), which ORs it with the
// server-driven `features` array.
//
// Separately, `resolveFeature` also honors `SERVER_WILDCARD_FEATURE`: a
// server-driven wildcard the dev/CI/simulator `App` shell (`bin/ab run App`)
// sends when its `ALL_FEATURES` env var is set, or a real native shell sends
// when its own opt-in env var is set (`LOGBOOK_ALL_FEATURES` on iOS/Mac, e.g.
// via `Config/MacDebug.xcconfig` for a development build -- see
// `LogbookNativeAppFeature.allFeatures` in Sources/LogbookApp/NativeApp.swift).
// When present in the server `features` array, it turns every flag on except
// those listed in `WILDCARD_EXCLUDED_FEATURES` below.
//
// The dev `App` shell can also inject one or more SPECIFIC flags BY NAME into
// the server `features` array via its `FEATURES=` env var (comma/whitespace
// separated), e.g. `FEATURES=experimental-view bin/ab run App` -- see
// `LogbookNativeApp.extraFeatures` / `LogbookApp.sessionFeatures`. A by-name
// server feature is honored by `resolveFeature` directly (the last clause
// below), so -- unlike the wildcard -- it CAN turn on a
// `WILDCARD_EXCLUDED_FEATURES` flag by name. This is the CLI/server-side way to
// exercise an in-progress excluded flag in a running app.
const STORAGE_KEY = "logbook-features";
const QUERY_PARAM = "features";
export const SERVER_WILDCARD_FEATURE = "all-features";

// Flags forced on for everyone, in the shipped bundle -- unlike the dev-only
// overrides below. Add a flag name here as an intermediate step before
// deleting its guard entirely, once a rollout is fully verified but the
// `hasFeature`/`hasClientFeature` checks haven't been ripped out of the code
// yet. Keep this empty while every guarded rollout still requires an explicit opt-in.
export const ALWAYS_ON_FEATURES: string[] = [];

// Flags carved out of the `SERVER_WILDCARD_FEATURE` blanket grant -- a flag
// still mid-rollout, not yet ready to ship broadly just because a Debug build
// turns everything else on. The explicit server flag and client dev-override
// paths still work, so a developer can turn an excluded flag on by name.
// Release-disabled flags deliberately stay out of this list:
// Release Test configs have no wildcard, while Debug wildcard builds must
// keep granting those flags so unfinished features remain easy to verify.
// Exclude a flag only when even a development build should not see it.
// Remove a flag's entry once its rollout is ready to ride along with the
// wildcard.
export const WILDCARD_EXCLUDED_FEATURES: string[] = [];

interface FeatureOverrides {
  enabled: string[];
  disabled: string[];
}

function splitFeatureOverrides(features: readonly string[]): FeatureOverrides {
  const enabled: string[] = [];
  const disabled: string[] = [];
  for (const feature of features) {
    if (feature.startsWith("-") && feature.length > 1) {
      disabled.push(feature.slice(1));
    } else {
      enabled.push(feature);
    }
  }
  return { enabled, disabled };
}

function clientFeatureOverrides(): FeatureOverrides {
  const features = [
    ...parseFeatureList(localStorageValue(STORAGE_KEY)),
    ...(typeof window !== "undefined" && window.location
      ? new URLSearchParams(window.location.search).getAll(QUERY_PARAM).flatMap(parseFeatureList)
      : []),
  ];
  return splitFeatureOverrides(features);
}

// Recomputed on every call rather than cached, since the localStorage/query
// overrides are only ever read by developers and may change within a session
// (e.g. a developer toggling `logbook-features` in devtools).
function clientFeatures(): Set<string> {
  const overrides = clientFeatureOverrides();
  return resolveFeatureFlags({
    known: [
      ...ALWAYS_ON_FEATURES,
      ...overrides.enabled,
      ...overrides.disabled,
    ],
    explicit: overrides.enabled,
    alwaysOn: ALWAYS_ON_FEATURES,
    excluded: overrides.disabled,
  });
}

export function hasClientFeature(feature: string): boolean {
  return clientFeatures().has(feature);
}

// Combines the server-driven feature list (from login) with the client-side
// dev override -- either source turning a flag on is enough. This is what
// `App.hasFeature` delegates to.
export function resolveFeature(serverFeatures: string[] | undefined, feature: string): boolean {
  const server = splitFeatureOverrides(serverFeatures ?? []);
  const client = clientFeatureOverrides();
  const disabled = [...server.disabled, ...client.disabled];
  const explicit = resolveFeatureFlags({
    known: [feature],
    explicit: [...server.enabled, ...client.enabled],
    alwaysOn: ALWAYS_ON_FEATURES,
    excluded: disabled,
  }).has(feature);
  if (explicit) {
    return true;
  }
  return !disabled.includes(feature)
    && server.enabled.includes(SERVER_WILDCARD_FEATURE)
    && !WILDCARD_EXCLUDED_FEATURES.includes(feature);
}
